import type { LocationOption } from '@provider-directory/components/location-selector/location-selector.component';
import type { LocationResource } from '@provider-directory/types/locations.contracts';

const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

const hasOnlineScheduling = (location: LocationOption): boolean =>
  (location.availability ?? 0) > 0;

const getLocationName = (location: LocationOption): string => {
  if (location.title && location.title.trim().length > 0) {
    return location.title;
  }
  return location.label ?? '';
};

const compareByName = (a: LocationOption, b: LocationOption): number =>
  nameCollator.compare(getLocationName(a), getLocationName(b));

const sortByAvailabilityThenName = (
  locations: LocationOption[],
): LocationOption[] => {
  const bookable: LocationOption[] = [];
  const nonBookable: LocationOption[] = [];

  for (const loc of locations) {
    if (hasOnlineScheduling(loc)) {
      bookable.push(loc);
    } else {
      nonBookable.push(loc);
    }
  }

  bookable.sort((a, b) => {
    const availDiff = (b.availability ?? 0) - (a.availability ?? 0);
    return availDiff !== 0 ? availDiff : compareByName(a, b);
  });

  nonBookable.sort(compareByName);

  return [...bookable, ...nonBookable];
};

const sortByPrimaryThenName = (
  locations: LocationOption[],
): LocationOption[] => {
  const primary: LocationOption[] = [];
  const nonPrimary: LocationOption[] = [];

  for (const loc of locations) {
    if (loc.isPrimaryLocation) {
      primary.push(loc);
    } else {
      nonPrimary.push(loc);
    }
  }

  nonPrimary.sort(compareByName);

  return [...primary, ...nonPrimary];
};

/**
 * Sorts location options for dropdown selectors (Provider Details hero,
 * Select Time modal, Select Time checkout).
 *
 * Rule 1: Selected/featured location always appears first.
 * Rule 2 (provider has ≥1 location with online scheduling):
 *   - Locations with online scheduling sorted most→least available (BE availability score desc)
 *   - Locations without online scheduling sorted alphabetically A→Z
 * Rule 3 (no locations with online scheduling):
 *   - Primary location first
 *   - Remaining locations alphabetically A→Z
 */
export const sortLocationOptionsForDropdown = (
  locations: LocationOption[],
  selectedLocationValue?: string,
): LocationOption[] => {
  if (locations.length <= 1) {
    return locations;
  }

  const providerHasOnlineScheduling = locations.some(hasOnlineScheduling);

  const selected = selectedLocationValue
    ? locations.find((loc) => loc.value === selectedLocationValue)
    : undefined;

  const rest = selected
    ? locations.filter((loc) => loc.value !== selectedLocationValue)
    : [...locations];

  const sorted = providerHasOnlineScheduling
    ? sortByAvailabilityThenName(rest)
    : sortByPrimaryThenName(rest);

  return selected ? [selected, ...sorted] : sorted;
};

const compareByPrimaryThenName = <T extends LocationResource>(
  a: T,
  b: T,
): number => {
  if (a.isPrimaryLocation !== b.isPrimaryLocation) {
    return a.isPrimaryLocation ? -1 : 1;
  }
  return nameCollator.compare(a.name, b.name);
};

/**
 * Sorts locations for the Provider Details Locations section.
 * Primary location first, then alphabetically by name A→Z.
 */
export const sortLocationsForDetails = <T extends LocationResource>(
  locations: T[],
): T[] => {
  if (locations.length <= 1) {
    return locations;
  }

  return [...locations].sort(compareByPrimaryThenName);
};
